import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  FileButton,
  Group,
  List,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconCheck, IconSend, IconUpload, IconUsers } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { koliko } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { zadevaIzHtml } from './okroznica';
import { PoslanaEposta } from './PoslanaEposta';
import { glave, obNapaki } from './zeton';

type Rezultat = Shema['OkroznicaRezultat'];

/**
 * Skrbništvo → Okrožnice: sporočilo vsem strankam (npr. novosti) iz zadeve in vsebine HTML (vstavljene ali naložene
 * datoteke), predogled, preizkus na en naslov, nato seznam prejemnikov (aktivni uporabniki aktivnih instanc in e-pošta
 * podjetij) z izbiro in potrditvijo. Strežnik pošlje okrožnico v skriti kopiji, odgovori gredo na naslov za odgovore iz
 * zavihka E-pošta; vsako sporočilo je v evidenci (spodaj) s seznamom prejemnikov.
 */
export function OkroznicaSkrbnistvo() {
  const odjemalec = useQueryClient();
  const [zadeva, setZadeva] = useState('');
  const [html, setHtml] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [izbiranje, setIzbiranje] = useState(false);
  const [izbrani, setIzbrani] = useState<Set<string> | null>(null);
  const [potrditev, setPotrditev] = useState(false);
  const [dela, setDela] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [rezultat, setRezultat] = useState<Rezultat | null>(null);

  const nastavitve = useQuery({
    queryKey: ['skrbnik', 'eposta'],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/eposta', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const prejemniki = useQuery({
    queryKey: ['skrbnik', 'okroznica', 'prejemniki'],
    enabled: izbiranje,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/okroznica/prejemniki', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const vsi = prejemniki.data ?? [];
  // dokler skrbnik ničesar ne spremeni, so izbrani vsi
  const izbor = izbrani ?? new Set(vsi.map((p) => p.naslov ?? ''));
  const izbraniSeznam = vsi.map((p) => p.naslov ?? '').filter((n) => izbor.has(n));
  const odgovori = nastavitve.data?.odgovori || nastavitve.data?.posiljatelj;

  const naloziDatoteko = async (f: File | null) => {
    if (!f) {
      return;
    }
    const vsebina = await f.text();
    setHtml(vsebina);
    if (!zadeva.trim()) {
      setZadeva(zadevaIzHtml(vsebina));
    }
  };

  const izberiPrejemnike = () => {
    const n: Record<string, string> = {};
    if (!zadeva.trim()) {
      n.zadeva = 'Vpišite zadevo.';
    }
    if (!html.trim()) {
      n.html = 'Vstavite vsebino sporočila (HTML) ali naložite datoteko.';
    }
    setNapake(n);
    if (Object.keys(n).length === 0) {
      setIzbiranje(true);
      setRezultat(null);
    }
  };

  const poslji = async () => {
    setDela(true);
    setNapaka(null);
    try {
      const r = await podatki(
        api.POST('/api/skrbnik/okroznica', { headers: glave(), body: { zadeva, html, prejemniki: izbraniSeznam } }),
      );
      setRezultat(r);
      setPotrditev(false);
      if (r.neposlani?.length) {
        setIzbrani(new Set(r.neposlani));
      }
    } catch (e) {
      obNapaki(e);
      setPotrditev(false);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
        setNapaka(Object.values(e.polja).join(' '));
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
      void odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'eposta-poslana'] });
    }
  };

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Kartica naslov="Nova okrožnica">
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Sporočilo vsem strankam, npr. o novostih. Vsebino prilepite kot HTML ali naložite datoteko (zadeva se
              izpolni iz njenega naslova). Navadno besedilo za odjemalce brez HTML sestavi strežnik.
            </Text>
            <TextInput
              label="Zadeva"
              value={zadeva}
              onChange={(e) => setZadeva(e.currentTarget.value)}
              error={napake.zadeva}
            />
            <Group justify="space-between" align="flex-end">
              <Text size="sm" fw={500}>
                Vsebina (HTML)
              </Text>
              <FileButton onChange={(f) => void naloziDatoteko(f)} accept=".html,.htm,text/html">
                {(p) => (
                  <Button {...p} variant="default" size="xs" leftSection={<IconUpload size={16} />}>
                    Naloži datoteko HTML
                  </Button>
                )}
              </FileButton>
            </Group>
            <Textarea
              aria-label="Vsebina (HTML)"
              value={html}
              onChange={(e) => setHtml(e.currentTarget.value)}
              error={napake.html}
              autosize
              minRows={8}
              maxRows={16}
              styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
            />
            {nastavitve.data &&
              (nastavitve.data.odgovori ? (
                <Text size="sm">
                  Odgovori gredo na <b>{nastavitve.data.odgovori}</b>.
                </Text>
              ) : (
                <Alert color="yellow" variant="light">
                  Naslov za odgovore ni nastavljen, zato gredo odgovori pošiljatelju (
                  {nastavitve.data.posiljatelj ?? 'ni vpisan'}). Nastavite ga v zavihku E-pošta.
                </Alert>
              ))}
            <Preizkus zadeva={zadeva} html={html} napakeVsebine={setNapake} />
            <Group justify="flex-end">
              <Button leftSection={<IconUsers size={18} />} onClick={izberiPrejemnike}>
                Izberi prejemnike
              </Button>
            </Group>
          </Stack>
        </Kartica>
        <Kartica naslov="Predogled">
          {html.trim() ? (
            <iframe
              title="Predogled okrožnice"
              sandbox=""
              srcDoc={html}
              style={{ width: '100%', height: 640, border: 0, borderRadius: 8, background: '#fff' }}
            />
          ) : (
            <Text size="sm" c="dimmed">
              Ko vstavite vsebino, bo tu prikazano, kako bo sporočilo videti.
            </Text>
          )}
        </Kartica>
      </SimpleGrid>

      {izbiranje && (
        <Kartica
          naslov="Prejemniki"
          dejanja={
            vsi.length > 0 && (
              <Text size="sm" c="dimmed">
                izbranih {izbraniSeznam.length} od {vsi.length}
              </Text>
            )
          }
        >
          {!prejemniki.data ? (
            <StanjeNalaganja
              napaka={prejemniki.error}
              ponovi={() => void prejemniki.refetch()}
              naslov="Prejemnikov ni bilo mogoče naložiti"
            />
          ) : (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Aktivni uporabniki aktivnih instanc (uporabniško ime ali e-pošta v profilu) in e-pošta podjetij iz
                nastavitev; vsak naslov samo enkrat. Okrožnica gre na naš naslov, prejemniki pa v skrito kopijo, zato
                drug drugega ne vidijo.
              </Text>
              {vsi.length === 0 ? (
                <Text size="sm">Ni prejemnikov z veljavnim e-poštnim naslovom.</Text>
              ) : (
                <Table.ScrollContainer minWidth={560}>
                  <Table verticalSpacing={6} highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={40}>
                          <Checkbox
                            aria-label="Izberi vse"
                            checked={izbraniSeznam.length === vsi.length}
                            indeterminate={izbraniSeznam.length > 0 && izbraniSeznam.length < vsi.length}
                            onChange={(e) =>
                              setIzbrani(new Set(e.currentTarget.checked ? vsi.map((p) => p.naslov ?? '') : []))
                            }
                          />
                        </Table.Th>
                        <Table.Th>Naslov</Table.Th>
                        <Table.Th>Od kod</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {vsi.map((p) => {
                        const n = p.naslov ?? '';
                        return (
                          <Table.Tr key={n}>
                            <Table.Td>
                              <Checkbox
                                aria-label={`Pošlji na ${n}`}
                                checked={izbor.has(n)}
                                onChange={(e) => {
                                  const novo = new Set(izbor);
                                  if (e.currentTarget.checked) {
                                    novo.add(n);
                                  } else {
                                    novo.delete(n);
                                  }
                                  setIzbrani(novo);
                                }}
                              />
                            </Table.Td>
                            <Table.Td className="brez-preloma">{n}</Table.Td>
                            <Table.Td>
                              <Text size="sm" c="dimmed">
                                {(p.viri ?? []).map((v, i) => (
                                  <span key={i}>
                                    {i > 0 && '; '}
                                    <Anchor component={Link} to={`/skrbnistvo/${v.companyId}`} size="sm">
                                      {v.instanca}
                                    </Anchor>{' '}
                                    – {v.vrsta === 'PODJETJE' ? 'e-pošta podjetja' : `uporabnik ${v.uporabnik}`}
                                  </span>
                                ))}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
              {napaka && (
                <Alert color="red" variant="light" role="alert">
                  {napaka}
                </Alert>
              )}
              {rezultat && <IzidPosiljanja r={rezultat} />}
              <Group justify="flex-end">
                <Button
                  leftSection={<IconSend size={18} />}
                  disabled={izbraniSeznam.length === 0}
                  onClick={() => setPotrditev(true)}
                >
                  Pošlji {koliko(izbraniSeznam.length, ['prejemniku', 'prejemnikoma', 'prejemnikom', 'prejemnikom'])}
                </Button>
              </Group>
            </Stack>
          )}
        </Kartica>
      )}

      <PoslanaEposta vrsta="OKROZNICA" naslov="Poslane okrožnice" prazno="Ni še poslanih okrožnic." />

      <Modal opened={potrditev} onClose={() => setPotrditev(false)} title="Pošljem okrožnico?" size="lg" radius="lg">
        <Stack gap="sm">
          <Text size="sm">
            Okrožnica <b>»{zadeva.trim()}«</b> bo poslana v skriti kopiji na{' '}
            <b>{koliko(izbraniSeznam.length, ['naslov', 'naslova', 'naslove', 'naslovov'])}</b>. Odgovori gredo na{' '}
            <b>{odgovori ?? 'pošiljatelja'}</b>.
          </Text>
          <ScrollArea.Autosize mah={260} type="auto">
            <List size="sm" spacing={2} aria-label="Naslovi za pošiljanje">
              {izbraniSeznam.map((n) => (
                <List.Item key={n}>{n}</List.Item>
              ))}
            </List>
          </ScrollArea.Autosize>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPotrditev(false)}>
              Prekliči
            </Button>
            <Button leftSection={<IconSend size={18} />} loading={dela} onClick={() => void poslji()}>
              Pošlji
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function IzidPosiljanja({ r }: { r: Rezultat }) {
  const neposlani = r.neposlani ?? [];
  return (
    <Alert
      color={neposlani.length ? 'red' : 'green'}
      variant="light"
      role={neposlani.length ? 'alert' : undefined}
      icon={neposlani.length ? undefined : <IconCheck size={18} />}
    >
      <Stack gap={4}>
        <Text size="sm">
          Poslano na {koliko(r.poslano ?? 0, ['naslov', 'naslova', 'naslove', 'naslovov'])}.
          {r.odgovorNa ? ` Odgovori gredo na ${r.odgovorNa}.` : ''}
        </Text>
        {neposlani.length > 0 && (
          <>
            <Text size="sm">
              Ni poslano ({neposlani.length}): {neposlani.join(', ')}. Ti naslovi so zdaj izbrani, da jih lahko pošljete
              znova.
            </Text>
            {(r.napake ?? []).map((n, i) => (
              <Text key={i} size="xs" c="dimmed">
                {n}
              </Text>
            ))}
          </>
        )}
      </Stack>
    </Alert>
  );
}

function Preizkus({
  zadeva,
  html,
  napakeVsebine,
}: {
  zadeva: string;
  html: string;
  napakeVsebine: (n: Record<string, string>) => void;
}) {
  const odjemalec = useQueryClient();
  const [prejemnik, setPrejemnik] = useState('');
  const [napakaPolja, setNapakaPolja] = useState<string | undefined>();
  const [izid, setIzid] = useState<{ uspelo: boolean; besedilo: string } | null>(null);
  const [dela, setDela] = useState(false);

  const poslji = async () => {
    setDela(true);
    setNapakaPolja(undefined);
    setIzid(null);
    try {
      await izvedi(
        api.POST('/api/skrbnik/okroznica/preizkus', { headers: glave(), body: { zadeva, html, prejemnik } }),
      );
      napakeVsebine({});
      setIzid({ uspelo: true, besedilo: `Preizkus je poslan na ${prejemnik.trim()}.` });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapakaPolja(e.polja.prejemnik);
        napakeVsebine(e.polja);
      } else {
        setIzid({ uspelo: false, besedilo: sporocilo(e) });
      }
    } finally {
      setDela(false);
      void odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'eposta-poslana'] });
    }
  };

  return (
    <Stack gap="xs">
      <Group align="flex-start" gap="xs" wrap="nowrap">
        <TextInput
          aria-label="Preizkus na naslov"
          placeholder="Naslov za preizkus"
          value={prejemnik}
          onChange={(e) => setPrejemnik(e.currentTarget.value)}
          error={napakaPolja}
          style={{ flex: 1 }}
        />
        <Button variant="default" onClick={() => void poslji()} loading={dela}>
          Pošlji preizkus
        </Button>
      </Group>
      {izid && (
        <Alert
          color={izid.uspelo ? 'green' : 'red'}
          variant="light"
          role={izid.uspelo ? undefined : 'alert'}
          icon={izid.uspelo ? <IconCheck size={18} /> : undefined}
        >
          {izid.besedilo}
        </Alert>
      )}
    </Stack>
  );
}
